import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

const PERIOD_TABS = ['Daily','Weekly','Monthly','Quarterly','Yearly'];

export default function Reports() {
  const navigate = useNavigate();
  const [activeTab,    setActiveTab]    = useState('Daily Sheet');
  const [periodTab,    setPeriodTab]    = useState('Daily');
  const [jobs,         setJobs]         = useState([]);
  const [dealerships,  setDealerships]  = useState([]);
  const [locations,    setLocations]    = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [downloading,  setDownloading]  = useState(false);
  const [filters, setFilters] = useState({
    from: new Date().toISOString().split('T')[0],
    to:   new Date().toISOString().split('T')[0],
    dealership: '',
    location:   '',
  });

  useEffect(() => {
    api.get('/jobs/dealerships').then(({data})=>setDealerships(data.dealerships)).catch(()=>{});
    api.get('/jobs/locations').then(({data})=>setLocations(data.locations)).catch(()=>{});
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from:filters.from, to:filters.to, limit:1000 };
      if (filters.dealership) params.dealership = filters.dealership;
      if (filters.location)   params.location   = filters.location;
      const { data } = await api.get('/jobs', { params });
      setJobs(data.jobs || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (activeTab === 'Daily Sheet') fetchJobs(); }, [fetchJobs, activeTab]);

  const setPreset = (preset) => {
    const today = new Date();
    const fmt = d => d.toISOString().split('T')[0];
    switch (preset) {
      case 'today':   setFilters(f=>({...f,from:fmt(today),to:fmt(today)})); break;
      case 'week':  { const s=new Date(today); s.setDate(today.getDate()-today.getDay()); setFilters(f=>({...f,from:fmt(s),to:fmt(today)})); break; }
      case 'month': { const s=new Date(today.getFullYear(),today.getMonth(),1); setFilters(f=>({...f,from:fmt(s),to:fmt(today)})); break; }
      case 'quarter':{ const q=Math.floor(today.getMonth()/3); const s=new Date(today.getFullYear(),q*3,1); setFilters(f=>({...f,from:fmt(s),to:fmt(today)})); break; }
      case 'year':  { const s=new Date(today.getFullYear(),0,1); setFilters(f=>({...f,from:fmt(s),to:fmt(today)})); break; }
    }
  };

  // ── Download report as CSV ──
  const downloadReport = () => {
    if (!jobs.length) return;
    setDownloading(true);
    try {
      const headers = ['Date','Dealership','Location','Stock #','VIN','Rego','Services','Price','PO Number','Invoice','Status'];
      const rows = jobs.map(j => [
        new Date(j.createdAt).toLocaleDateString('en-AU'),
        j.dealership||'',
        j.location||'',
        j.stockNum||'',
        j.vinNum||'',
        j.rego||'',
        (j.coreServices||[]).join(' | '),
        (j.price||0).toFixed(2),
        j.poNumber||'',
        j.invoiceNumber||'',
        j.status||'',
      ]);
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type:'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const from = filters.from.replace(/-/g,'');
      const to   = filters.to.replace(/-/g,'');
      a.href     = url;
      a.download = `CarSaloon-Report-${from}-to-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  };

  // Revenue helpers
  const totalRevenue = jobs.reduce((a,j)=>a+(j.price||0),0);

  const groupBy = (jobs, keyFn) => {
    const map = {};
    jobs.forEach(j => {
      const k = keyFn(j);
      if (!map[k]) map[k] = { label:k, jobs:[], revenue:0 };
      map[k].jobs.push(j); map[k].revenue += j.price||0;
    });
    return Object.values(map).sort((a,b) => b.revenue - a.revenue);
  };

  const getWeek    = (d) => { const dt=new Date(d); const s=new Date(dt); s.setDate(dt.getDate()-dt.getDay()); return `Wk ${s.toLocaleDateString('en-AU',{day:'2-digit',month:'short'})}`; };
  const getQuarter = (d) => { const dt=new Date(d); return `Q${Math.floor(dt.getMonth()/3)+1} ${dt.getFullYear()}`; };

  const getPeriodGroups = () => {
    switch (periodTab) {
      case 'Daily':     return groupBy(jobs, j=>new Date(j.createdAt).toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric'}));
      case 'Weekly':    return groupBy(jobs, j=>getWeek(j.createdAt));
      case 'Monthly':   return groupBy(jobs, j=>new Date(j.createdAt).toLocaleDateString('en-AU',{month:'long',year:'numeric'}));
      case 'Quarterly': return groupBy(jobs, j=>getQuarter(j.createdAt));
      case 'Yearly':    return groupBy(jobs, j=>String(new Date(j.createdAt).getFullYear()));
      default:          return [];
    }
  };

  const dealershipGroups = groupBy(jobs, j=>j.dealership);
  const periodGroups     = getPeriodGroups();

  const PRESETS = [
    { key:'today',   label:'Today' },
    { key:'week',    label:'This Week' },
    { key:'month',   label:'This Month' },
    { key:'quarter', label:'This Quarter' },
    { key:'year',    label:'This Year' },
  ];

  return (
    <div style={{minHeight:'100vh',background:'var(--bg)',fontFamily:'Inter,sans-serif'}}>

      {/* Header */}
      <div style={{background:'var(--surface)',borderBottom:'1px solid var(--border)',padding:'12px 16px',display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',rowGap:'10px'}}>
        <button onClick={()=>navigate(-1)} style={backBtn}>← Back</button>
        <div>
          <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'16px'}}>📊 Reports</div>
          <div style={{color:'var(--muted)',fontSize:'11px'}}>Revenue & activity reports</div>
        </div>
        <div style={{marginLeft:'auto',display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
          {/* Download button */}
          {jobs.length > 0 && (
            <button onClick={downloadReport} disabled={downloading}
              style={{background:'var(--green)',border:'none',borderRadius:'var(--radius)',padding:'8px 16px',color:'#fff',fontWeight:'500',fontSize:'13px',cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'6px'}}>
              {downloading ? '⏳' : '📥'} Download Report
            </button>
          )}
          {/* Report type tabs */}
          {['Daily Sheet','Inspections'].map(t=>(
            <button key={t} onClick={()=>setActiveTab(t)}
              style={{padding:'7px 16px',borderRadius:'var(--radius)',border:`1.5px solid ${activeTab===t?'var(--blue)':'var(--border-md)'}`,background:activeTab===t?'rgba(0,119,168,0.08)':'#fff',color:activeTab===t?'var(--blue)':'var(--muted)',fontWeight:activeTab===t?700:400,fontSize:'13px',cursor:'pointer',fontFamily:'inherit'}}>
              {t==='Daily Sheet'?'📄':'📋'} {t}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'Daily Sheet' && (
        <div className="stack-mobile" style={{display:'flex',maxWidth:'1200px',margin:'0 auto',padding:'20px 16px',gap:'20px',alignItems:'flex-start'}}>

          {/* ── LEFT SIDEBAR ── */}
          <div className="full-mobile static-mobile" style={{width:'260px',flexShrink:0,position:'sticky',top:'72px',display:'flex',flexDirection:'column',gap:'12px'}}>

            {/* Quick presets */}
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
              <div style={{background:'var(--bg)',borderBottom:'1px solid var(--border)',padding:'10px 14px'}}>
                <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'13px'}}>⚡ Quick Select</div>
              </div>
              {PRESETS.map(p=>(
                <button key={p.key} onClick={()=>setPreset(p.key)}
                  style={{display:'block',width:'100%',textAlign:'left',padding:'10px 14px',border:'none',borderBottom:'1px solid var(--bg)',background:'var(--surface)',color:'var(--text)',fontSize:'13px',cursor:'pointer',fontFamily:'inherit'}}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'}
                  onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                  {p.label}
                </button>
              ))}
            </div>

            {/* Date range */}
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
              <div style={{background:'var(--bg)',borderBottom:'1px solid var(--border)',padding:'10px 14px'}}>
                <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'13px'}}>📅 Date Range</div>
              </div>
              <div style={{padding:'12px 14px',display:'flex',flexDirection:'column',gap:'10px'}}>
                <div>
                  <label style={lbl}>From</label>
                  <input type="date" value={filters.from} onChange={e=>setFilters(f=>({...f,from:e.target.value}))} style={inp}/>
                </div>
                <div>
                  <label style={lbl}>To</label>
                  <input type="date" value={filters.to} onChange={e=>setFilters(f=>({...f,to:e.target.value}))} style={inp}/>
                </div>
              </div>
            </div>

            {/* Dealership filter */}
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
              <div style={{background:'var(--bg)',borderBottom:'1px solid var(--border)',padding:'10px 14px'}}>
                <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'13px'}}>🏢 Dealership</div>
              </div>
              <div style={{padding:'12px 14px'}}>
                <select value={filters.dealership} onChange={e=>setFilters(f=>({...f,dealership:e.target.value}))} style={inp}>
                  <option value="">All Dealerships</option>
                  {dealerships.map(d=><option key={d._id} value={d.name}>{d.name}</option>)}
                </select>
              </div>
            </div>

            {/* Location filter */}
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
              <div style={{background:'var(--bg)',borderBottom:'1px solid var(--border)',padding:'10px 14px'}}>
                <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'13px'}}>📍 Location</div>
              </div>
              <div style={{padding:'12px 14px'}}>
                <select value={filters.location} onChange={e=>setFilters(f=>({...f,location:e.target.value}))} style={inp}>
                  <option value="">All Locations</option>
                  {locations.map(l=><option key={l._id} value={l.name}>{l.name}</option>)}
                </select>
              </div>
            </div>

            {/* Revenue by Dealership — in sidebar */}
            {!loading && dealershipGroups.length > 0 && (
              <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
                <div style={{background:'var(--bg)',borderBottom:'1px solid var(--border)',padding:'10px 14px'}}>
                  <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'13px'}}>💰 Revenue by Dealership</div>
                </div>
                <div style={{padding:'12px 14px'}}>
                  {dealershipGroups.map(g=>(
                    <div key={g.label} style={{marginBottom:'10px'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'4px'}}>
                        <span style={{fontSize:'13px',fontWeight:'500',color:'var(--text)'}}>{g.label}</span>
                        <span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'500',color:'var(--green)',fontSize:'13px'}}>${g.revenue.toFixed(2)}</span>
                      </div>
                      <div style={{height:'5px',background:'var(--border)',borderRadius:'3px',overflow:'hidden'}}>
                        <div style={{height:'100%',background:'linear-gradient(90deg,var(--blue),var(--blue))',borderRadius:'3px',width:`${totalRevenue?Math.round(g.revenue/totalRevenue*100):0}%`}}/>
                      </div>
                      <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'2px'}}>{g.jobs.length} job{g.jobs.length!==1?'s':''} · {totalRevenue?Math.round(g.revenue/totalRevenue*100):0}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Revenue by Period — in sidebar */}
            {!loading && jobs.length > 0 && (
              <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
                <div style={{background:'var(--bg)',borderBottom:'1px solid var(--border)',padding:'10px 14px'}}>
                  <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'13px'}}>📅 Revenue by Period</div>
                </div>
                <div style={{padding:'10px 14px'}}>
                  {/* Period tabs */}
                  <div style={{display:'flex',gap:'4px',flexWrap:'wrap',marginBottom:'10px'}}>
                    {PERIOD_TABS.map(t=>(
                      <button key={t} onClick={()=>setPeriodTab(t)}
                        style={{padding:'3px 10px',borderRadius:'20px',border:`1px solid ${periodTab===t?'var(--blue)':'var(--border-md)'}`,background:periodTab===t?'rgba(0,119,168,0.08)':'var(--bg)',color:periodTab===t?'var(--blue)':'var(--muted)',fontWeight:periodTab===t?700:400,fontSize:'11px',cursor:'pointer',fontFamily:'inherit'}}>
                        {t}
                      </button>
                    ))}
                  </div>
                  {periodGroups.map((g,i)=>(
                    <div key={g.label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:'1px solid var(--bg)',fontSize:'12px'}}>
                      <span style={{color:'var(--text)'}}>{g.label}</span>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'500',color:'var(--green)'}}>${g.revenue.toFixed(2)}</div>
                        <div style={{color:'var(--muted)',fontSize:'11px'}}>{g.jobs.length} jobs</div>
                      </div>
                    </div>
                  ))}
                  <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0 0',borderTop:'1px solid var(--border-md)',marginTop:'4px'}}>
                    <span style={{fontSize:'13px',fontWeight:'500',color:'var(--blue)'}}>TOTAL</span>
                    <span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'600',color:'var(--green)',fontSize:'13px'}}>${totalRevenue.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Summary card */}
            {!loading && jobs.length > 0 && (
              <div style={{background:'var(--blue)',borderRadius:'var(--radius-lg)',padding:'14px 16px',color:'#fff'}}>
                <div style={{fontSize:'11px',fontWeight:'500',textTransform:'uppercase',letterSpacing:'1px',opacity:.8,marginBottom:'4px'}}>Summary</div>
                <div style={{fontSize:'1.5rem',fontWeight:'600',fontFamily:'JetBrains Mono,monospace'}}>${totalRevenue.toFixed(2)}</div>
                <div style={{fontSize:'12px',opacity:.8,marginTop:'4px'}}>{jobs.length} jobs · {filters.from===filters.to?filters.from:`${filters.from} → ${filters.to}`}</div>
              </div>
            )}
          </div>

          {/* ── RIGHT MAIN AREA — Full job list only ── */}
          <div style={{flex:1,minWidth:0}}>
            {loading ? (
              <div style={{textAlign:'center',padding:'80px',color:'var(--muted)',background:'var(--surface)',borderRadius:'var(--radius-lg)',border:'1px solid var(--border)'}}>
                <div style={{fontSize:'2rem',marginBottom:'12px'}}>⏳</div>
                Loading report...
              </div>
            ) : jobs.length === 0 ? (
              <div style={{textAlign:'center',padding:'80px',background:'var(--surface)',borderRadius:'var(--radius-lg)',border:'1px solid var(--border)'}}>
                <div style={{fontSize:'2.5rem',marginBottom:'12px'}}>📊</div>
                <div style={{color:'var(--blue)',fontWeight:'500',marginBottom:'6px'}}>No data for selected filters</div>
                <div style={{color:'var(--muted)',fontSize:'13px'}}>Try a different date range or filter</div>
              </div>
            ) : (
              <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
                <div style={{background:'var(--bg)',borderBottom:'1px solid var(--border)',padding:'12px 18px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'14px'}}>📋 All Jobs</div>
                  <span style={{background:'var(--blue-light)',color:'var(--blue)',fontSize:'11px',fontWeight:'500',padding:'3px 10px',borderRadius:'20px'}}>{jobs.length} records</span>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',minWidth:'750px'}}>
                    <thead>
                      <tr style={{background:'var(--bg)'}}>
                        {['Date','Dealership','Location','Stock #','VIN','Services','Price','PO','Invoice','Status'].map(h=>(
                          <th key={h} style={{padding:'9px 14px',textAlign:'left',fontSize:'11px',fontWeight:'500',textTransform:'uppercase',letterSpacing:'.8px',color:'var(--muted)',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job,i)=>(
                        <tr key={job._id} style={{background:i%2===0?'#fff':'#f9fbfc'}}>
                          <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontSize:'12px',color:'var(--muted)',whiteSpace:'nowrap'}}>{new Date(job.createdAt).toLocaleDateString('en-AU')}</span></td>
                          <td style={td}><span style={{color:'var(--blue)',fontWeight:'500',fontSize:'13px'}}>{job.dealership}</span></td>
                          <td style={td}><span style={{color:'var(--muted)',fontSize:'13px'}}>{job.location}</span></td>
                          <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'500',color:'var(--blue)'}}>{job.stockNum||'—'}</span></td>
                          <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontSize:'12px'}}>{job.vinNum||'—'}</span></td>
                          <td style={td}><span style={{fontSize:'12px'}}>{(job.coreServices||[]).join(', ')||'—'}</span></td>
                          <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'500',color:'var(--green)'}}>${(job.price||0).toFixed(2)}</span></td>
                          <td style={td}>
                            {job.poNumber
                              ? <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:'13px',color:'var(--green)',fontWeight:'500'}}>{job.poNumber}</span>
                              : <span style={{background:'var(--amber-bg)',color:'var(--amber)',fontSize:'11px',fontWeight:'500',padding:'2px 8px',borderRadius:'20px'}}>PENDING</span>}
                          </td>
                          <td style={td}>
                            {job.invoiceNumber
                              ? <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:'13px',color:'var(--purple)',fontWeight:'500'}}>{job.invoiceNumber}</span>
                              : <span style={{color:'var(--hint)',fontSize:'.75rem'}}>—</span>}
                          </td>
                          <td style={td}>
                            <span style={{fontSize:'11px',fontWeight:'500',padding:'3px 9px',borderRadius:'20px',background:job.status==='pending_po'?'rgba(217,119,6,0.1)':'rgba(22,163,74,0.1)',color:job.status==='pending_po'?'var(--amber)':'var(--green)',whiteSpace:'nowrap'}}>
                              {job.status==='pending_po'?'Pending PO':'Submitted'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'Inspections' && (
        <div style={{maxWidth:'800px',margin:'40px auto',padding:'0 16px',textAlign:'center'}}>
          <div style={{background:'var(--surface)',borderRadius:'var(--radius-lg)',border:'1px solid var(--border)',padding:'60px'}}>
            <div style={{fontSize:'2.5rem',marginBottom:'12px'}}>📋</div>
            <div style={{color:'var(--blue)',fontWeight:'500',marginBottom:'6px'}}>Inspection Reports</div>
            <div style={{color:'var(--muted)',fontSize:'13px'}}>Coming soon</div>
          </div>
        </div>
      )}
    </div>
  );
}

const backBtn = {background:'var(--bg)',border:'1px solid var(--border)',color:'var(--muted)',borderRadius:'var(--radius)',padding:'7px 14px',cursor:'pointer',fontSize:'13px',fontFamily:'inherit'};
const lbl     = {display:'block',fontSize:'11px',fontWeight:'500',textTransform:'uppercase',letterSpacing:'.8px',color:'var(--muted)',marginBottom:'6px'};
const inp     = {width:'100%',background:'var(--bg)',border:'1px solid var(--border-md)',borderRadius:'var(--radius)',padding:'8px 12px',color:'var(--text)',fontFamily:'Inter,sans-serif',fontSize:'13px',outline:'none',boxSizing:'border-box'};
const td      = {padding:'10px 14px',borderBottom:'1px solid rgba(0,0,0,0.04)',verticalAlign:'middle'};
